<template>
  <div class="block-gallery-preview">
    <component :is="resolvedComponent" v-bind="resolvedProps" />
  </div>
</template>

<script>
import Block from './Block.vue';
import BlockCollector from './BlockCollector.vue';
import BlockCountdown from './BlockCountdown.vue';
import BlockCounter from './BlockCounter.vue';
import BlockLink from './BlockLink.vue';
import BlockProgress from './BlockProgress.vue';
import BlockTodoList from './BlockTodoList.vue';

const COMPONENTS = {
  Block,
  BlockCollector,
  BlockCountdown,
  BlockCounter,
  BlockLink,
  BlockProgress,
  BlockTodoList,
};

function defaultSizeForType(type) {
  switch (type) {
    case 'Block':
      return 2;
    case 'BlockProgress':
      return 2;
    case 'BlockLink':
      return 2;
    case 'BlockCounter':
      return 2;
    case 'BlockCollector':
      return 2;
    case 'BlockCountdown':
      return 120;
    case 'BlockTodoList':
      return 2;
    default:
      return 2;
  }
}

export default {
  name: 'BlockGalleryBlockPreview',
  props: {
    preset: {
      type: Object,
      required: true,
    },
    idPrefix: {
      type: String,
      default: 'preview',
    },
    index: {
      type: Number,
      default: 0,
    },
    sizeOverride: {
      type: Number,
      default: null,
    },
  },
  computed: {
    resolvedComponent() {
      const type = this.preset && this.preset.type ? String(this.preset.type) : 'Block';
      return COMPONENTS[type] || Block;
    },
    resolvedProps() {
      const p = this.preset || {};

      const type = p.type ? String(p.type) : 'Block';

      const counters = Array.isArray(p.counters) ? p.counters : [];

      // Keep previews deterministic and non-interactive.
      const id = `${this.idPrefix}_${this.index}`;
      const size = typeof this.sizeOverride === 'number' ? this.sizeOverride : p.size;

      return {
        id,
        title: p.title || '',
        color: p.color || 'gray',
        size: typeof size === 'number' ? size : defaultSizeForType(type),
        faceType: p.faceType || 'default',

        // Block-type specific fields
        counters: (type === 'BlockCounter' || type === 'BlockProgress') ? counters : undefined,
        increaseValue: typeof p.increaseValue === 'number' ? p.increaseValue : undefined,
        blockTick: typeof p.blockTick === 'number' ? p.blockTick : undefined,

        progressStart: typeof p.progressStart === 'number' ? p.progressStart : (type === 'BlockProgress' ? Date.now() : undefined),
        progressEnd: typeof p.progressEnd === 'number' ? p.progressEnd : (type === 'BlockProgress' ? 0 : undefined),

        link: p.link || undefined,
        imageLink: p.imageLink || undefined,

        todos: type === 'BlockTodoList' ? (Array.isArray(p.todos) ? p.todos : []) : undefined,
        todoHistory: type === 'BlockTodoList' ? (Array.isArray(p.todoHistory) ? p.todoHistory : []) : undefined,

        // Common flags
        preview: true,
        buildMode: true,
        embed: false,
        embedPreview: false,
        isNew: false,
        isLoading: false,
        hasError: false,

        // Common optional props used across blocks
        elapsedTime: 0,
        totalTime: typeof p.totalTime === 'number' ? p.totalTime : undefined,
        activeSince: typeof p.activeSince === 'number' ? p.activeSince : undefined,
        activeCountdownSince: typeof p.activeCountdownSince === 'number' ? p.activeCountdownSince : undefined,
        activeBreakSince: typeof p.activeBreakSince === 'number' ? p.activeBreakSince : undefined,
        notificationSent: !!p.notificationSent,

        elements: Array.isArray(p.elements) ? p.elements : [],
        allowTemplateAdd: !!p.allowTemplateAdd,
        refID: p.refID || undefined,
        embedOptions: p.embedOptions || undefined,
      };
    },
  },
};
</script>

<style scoped>
/* Keep previews visually contained inside cards */
.block-gallery-preview {
  display: inline-block;
}
</style>
